import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  alphaNumericUppercase,
  handlePasteText,
} from "../../../CommonMethhods/CommonMethods";

// Copy + icon for each checker action, keyed by the action name.
const ACTION_CONFIG = {
  approve: {
    confirmText: "Are you sure you want to approve this request",
    successText: "Request approved successfully",
    warnClass: "warn",
    successClass: "success",
    needsRemarks: false,
  },
  reject: {
    confirmText: "Are you sure you want to reject this request",
    successText: "Request rejected successfully",
    warnClass: "danger",
    successClass: "danger",
    needsRemarks: true,
  },
  sendback: {
    confirmText: "Are you sure you want to send back this request",
    successText: "Request send back successfully",
    warnClass: "warn",
    successClass: "success",
    needsRemarks: true,
  },
};

const MIN_REMARKS = 10;
const MAX_REMARKS = 500;

// Approve / Reject / Send back flow for the checker screens:
//   confirm -> (remarks, for reject & send back) -> success.
// `onSubmit(action, reason)` performs the API call and resolves true on success;
// `onDone` runs when the success popup is dismissed.
function CheckerActionModals({ action, onCancel, onSubmit, onDone }) {
  const [step, setStep] = useState("confirm");
  const [remarksInput, setRemarksInput] = useState("");
  // Set once the checker has typed, so an emptied box can say it is required
  // without showing the error on a box nobody has touched yet.
  const [remarksTouched, setRemarksTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStep("confirm");
    setRemarksInput("");
    setRemarksTouched(false);
    setSubmitting(false);
  }, [action]);

  const config = ACTION_CONFIG[action];
  if (!config) return null;

  const remarksLength = remarksInput.trim().length;
  const remainingChars = MAX_REMARKS - remarksInput.length;
  const showMinCharsMsg = remarksLength > 0 && remarksLength < MIN_REMARKS;
  const showRequiredMsg = remarksTouched && remarksLength === 0;

  const submit = async (reason = "") => {
    setSubmitting(true);
    const ok = await onSubmit(action, reason);
    setSubmitting(false);
    if (ok) setStep("success");
  };

  const handleConfirm = () => {
    if (config.needsRemarks) {
      setStep("remarks");
    } else {
      submit();
    }
  };

  const handleRemarksSubmit = () => {
    if (remarksLength < MIN_REMARKS || submitting) return;
    submit(remarksInput.trim());
  };

  const renderHeader = (title, onClose) => (
    <div className="checker-action-header">
      <span className="checker-action-title">{title}</span>
      <button type="button" className="checker-action-close" aria-label="Close" onClick={onClose}>
        <i className="fa fa-times" />
      </button>
    </div>
  );

  return (
    <Modal
      show
      centered
      backdrop="static"
      keyboard={false}
      className={`checker-action-modal ${step === "remarks" ? "remarks-step" : ""}`}
    >
      <Modal.Body>
        {step === "confirm" && (
          <>
            {renderHeader("Approval Review", onCancel)}
            <div className="checker-action-content">
              <span className={`checker-action-icon ${config.warnClass}`}>
                <i className="fa fa-exclamation" />
              </span>
              <span className="checker-action-text">{config.confirmText}</span>
            </div>
            <div className="checker-action-footer">
              <button type="button" className="back-btn" onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="submit-btns" onClick={handleConfirm}>
                Confirm
              </button>
            </div>
          </>
        )}

        {step === "remarks" && (
          <>
            {renderHeader("Add Remarks", onCancel)}
            <textarea
              className="checker-action-textarea"
              placeholder="Add Remarks"
              value={remarksInput}
              rows={4}
              minLength={MIN_REMARKS}
              maxLength={MAX_REMARKS}
              autoFocus
              onChange={(e) => {
                setRemarksInput(alphaNumericUppercase(e.target.value, MAX_REMARKS));
                setRemarksTouched(true);
              }}
              onPaste={handlePasteText({
                value: remarksInput,
                setValue: setRemarksInput,
                maxLength: MAX_REMARKS,
                regex: /[^A-Z0-9\s]/g,
              })}
            />
            {/* <div className={`char-count ${remarksLength < MIN_REMARKS ? "char-count-error" : ""}`}>
              {remarksLength < MIN_REMARKS ? (
                <span>
                  Minimum {MIN_REMARKS - remarksLength} character
                  {MIN_REMARKS - remarksLength > 1 ? "s are" : " is"} required (
                  {remarksInput.length}/{MAX_REMARKS})
                </span>
              ) : (
                <span>{remainingChars} characters remaining</span>
              )}
            </div> */}
            {/* Same wording as the maker's remarks box: a fixed message while
                the checker is under the minimum, "required" once it is emptied. */}
            <div className={`char-count ${showMinCharsMsg || showRequiredMsg ? "char-count-error" : ""}`}>
              {showRequiredMsg ? (
                <span>Remarks is required</span>
              ) : showMinCharsMsg ? (
                <span>Minimum {MIN_REMARKS} characters are required</span>
              ) : (
                <span>{remainingChars} characters remaining</span>
              )}
            </div>
            <div className="checker-action-footer end">
              <button type="button" className="back-btn" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="submit-btns"
                onClick={handleRemarksSubmit}
                disabled={remarksLength < MIN_REMARKS || submitting}
              >
                Submit
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            {renderHeader("Confirmation", onDone)}
            <div className="checker-action-content">
              <span className={`checker-action-icon ${config.successClass}`}>
                <i className={`fa ${config.successClass === "danger" ? "fa-times" : "fa-check"}`} />
              </span>
              <span className="checker-action-text">{config.successText}</span>
            </div>
            <div className="checker-action-footer">
              <button type="button" className="submit-btns full" onClick={onDone}>
                Done
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default CheckerActionModals;
